import { Fragment, useState } from "react";
import { asEntry, entryMeasure, hasVariants, MAX_REST_SEC, requiredFields, setFields, type Exercise } from "../core/exercises";
import { lastTimedDuration } from "../core/progress";
import type { Session } from "../core/sessions";
import type { Backend } from "../core/storage";
import {
  addExercises, addSet, moveItem, newTemplateDraft, removeSet, saveTemplate, optionalTargets, SET_TYPES, setLabels,
  switchMeasure, updateSet, type Template, type TemplateDraft, type TemplateError, type TemplateExercise, type TemplateSet,
} from "../core/templates";
import { formatNumber, parseDistance, parseReps, parseWeight } from "../core/units";
import { DEFAULT_SET_SEC } from "../core/workout";
import { ActionSheet } from "./ActionSheet";
import { useBack } from "./back";
import { ConfirmDialog } from "./ConfirmDialog";
import { DurationPicker } from "./DurationPicker";
import { ExercisePicker } from "./ExercisePicker";
import { NumField } from "./NumField";
import { useDragReorder } from "./useDragReorder";
import { formatDuration, t } from "./texts";

const REST_STEP = 15;

/**
 * The menu entry "Umstellen auf …" for an exercise with variants (todo 16): reps ↔ timed. None for other exercises.
 */
export function variantAction(e: Exercise | undefined, entry: { measure?: Exercise["measure"] }, onSwitch: (to: "reps" | "timed", e: Exercise) => void, disabled = false) {
  if (!e || !hasVariants(e)) return [];
  const to = entryMeasure(e, entry) === "timed" ? "reps" : "timed";
  return [{ label: t.template.switchTo(t.measure(to)), disabled, onClick: () => onSwitch(to, e) }];
}

/**
 * Create or edit a template, as Strong's editor: name and notes, then one card per exercise with its rest time and a
 * set table whose columns follow the exercise's measure. Tapping a set's label opens its type and "remove"; the
 * handle "≡" drags an exercise to another place.
 */
export function TemplateEditor(props: {
  db: Backend;
  exercises: Exercise[];
  /** for the duration a set gets when an exercise is switched to "Reps in Zeit" */
  sessions: Session[];
  initial?: Template;
  onCreatedExercise(e: Exercise): Promise<void>;
  onSaved(t: Template): void;
  onCancel(): void;
}) {
  const [draft, setDraft] = useState<TemplateDraft>(() => props.initial ?? newTemplateDraft());
  const [errors, setErrors] = useState<TemplateError[]>([]);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [exerciseMenu, setExerciseMenu] = useState<number>();
  const [setMenu, setSetMenu] = useState<{ ex: number; set: number }>();
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const byId = new Map(props.exercises.map((e) => [e.id, e]));
  const reorder = useDragReorder(draft.exercises.length,
    (from, to) => change((d) => ({ ...d, exercises: moveItem(d.exercises, from, to) })));

  const change = (fn: (d: TemplateDraft) => TemplateDraft) => { setDraft(fn); setErrors([]); };
  const changeExercise = (i: number, fn: (te: TemplateExercise) => TemplateExercise) =>
    change((d) => ({ ...d, exercises: d.exercises.map((te, j) => (j === i ? fn(te) : te)) }));

  const dirty = JSON.stringify(draft) !== JSON.stringify(props.initial ?? newTemplateDraft());
  const cancel = () => (dirty ? setConfirmDiscard(true) : props.onCancel());
  useBack(cancel);
  const save = async () => {
    setBusy(true);
    const r = await saveTemplate(props.db, draft, props.exercises);
    setBusy(false);
    if (r.ok) props.onSaved(r.template);
    else setErrors(r.errors);
  };

  if (picking) {
    return (
      <ExercisePicker db={props.db} exercises={props.exercises} onCreated={props.onCreatedExercise} onCancel={() => setPicking(false)}
        onAdd={(list) => { change((d) => addExercises(d, list)); setPicking(false); }} />
    );
  }

  const drag = reorder.drag;
  const menuEx = exerciseMenu !== undefined ? draft.exercises[exerciseMenu] : undefined;
  const menuSet = setMenu && draft.exercises[setMenu.ex];
  return (
    <>
      <header className="titlebar">
        <button className="ghost" onClick={cancel}>{t.cancel}</button>
        <button className="primary" disabled={busy} onClick={save}>{t.save}</button>
      </header>
      <h1>{props.initial ? t.template.titleEdit : t.template.titleNew}</h1>

      <label className="label">{t.template.name}
        <input className="field" value={draft.name} placeholder={t.template.namePlaceholder} autoFocus={!props.initial}
          onChange={(e) => change((d) => ({ ...d, name: e.target.value }))} />
      </label>
      {errors.includes("nameMissing") && <p className="error">{t.template.nameMissing}</p>}
      <label className="label">{t.template.notes}
        <textarea className="field" rows={2} value={draft.notes} placeholder={t.template.notesPlaceholder}
          onChange={(e) => change((d) => ({ ...d, notes: e.target.value }))} />
      </label>

      {/* while dragging, the cards are collapsed and moved out of the way by transforms (the DOM order stays) */}
      {draft.exercises.map((te, i) => (
        <ExerciseCard key={i} te={te} exercise={asEntry(byId.get(te.exerciseId), te)}
          collapsed={!!drag} dragging={drag?.from === i} shift={drag?.shift(i)}
          cardRef={reorder.item(i)} handle={reorder.handle(i)}
          onMenu={() => setExerciseMenu(i)}
          onChange={(fn) => changeExercise(i, fn)}
          onSetMenu={(set) => setSetMenu({ ex: i, set })} />
      ))}

      <button className="secondary wide" onClick={() => setPicking(true)}>{t.template.addExercise}</button>
      {errors.includes("restInvalid") && <p className="error">{t.template.restInvalid}</p>}
      {errors.includes("valueInvalid") && <p className="error">{t.template.valuesInvalid}</p>}
      <button className="primary wide" disabled={busy} onClick={save}>{t.save}</button>

      {menuEx && exerciseMenu !== undefined && (
        <ActionSheet title={byId.get(menuEx.exerciseId)?.name ?? t.template.unknownExercise} onClose={() => setExerciseMenu(undefined)}
          actions={[
            ...variantAction(byId.get(menuEx.exerciseId), menuEx, (to, e) => {
              const sec = lastTimedDuration(props.sessions, e) ?? DEFAULT_SET_SEC;
              changeExercise(exerciseMenu, (te) => switchMeasure(te, e, to, sec));
              setExerciseMenu(undefined);
            }),
            { label: t.template.removeExercise, danger: true,
              onClick: () => { change((d) => ({ ...d, exercises: d.exercises.filter((_, j) => j !== exerciseMenu) })); setExerciseMenu(undefined); } },
          ]} />
      )}
      {setMenu && menuSet && (
        <ActionSheet title={`${t.template.colSet} ${setLabels(menuSet.sets)[setMenu.set]}`} onClose={() => setSetMenu(undefined)}
          actions={[
            ...SET_TYPES.map((type) => ({
              label: (menuSet.sets[setMenu.set]?.type === type ? "✓ " : "") + t.setType[type],
              onClick: () => { changeExercise(setMenu.ex, (te) => updateSet(te, setMenu.set, { type })); setSetMenu(undefined); },
            })),
            { label: t.template.removeSet, danger: true,
              onClick: () => { changeExercise(setMenu.ex, (te) => removeSet(te, setMenu.set)); setSetMenu(undefined); } },
          ]} />
      )}
      {confirmDiscard && (
        <ConfirmDialog text={t.template.discard} confirm={t.template.discardYes} danger
          onCancel={() => setConfirmDiscard(false)} onConfirm={props.onCancel} />
      )}
    </>
  );
}

/**
 * One exercise with its set table. Also used by the session editor (`session`): there every column is a value done
 * (no "Ziel-…"), a timed set shows its time before the reps as in the workout log, there is no rest row and no drag.
 */
export function ExerciseCard(props: {
  te: TemplateExercise;
  exercise: Exercise | undefined;
  session?: boolean;
  /** sets marked red (session editor: the measure's value is missing) */
  missing?: number[];
  /** while any card is dragged: only the header */
  collapsed?: boolean;
  dragging?: boolean;
  /** vertical shift in px while a card is dragged */
  shift?: number;
  cardRef?(el: HTMLElement | null): void;
  handle?: ReturnType<ReturnType<typeof useDragReorder>["handle"]>;
  onMenu(): void;
  onChange(fn: (te: TemplateExercise) => TemplateExercise): void;
  onSetMenu(set: number): void;
}) {
  const { te, exercise: e } = props;
  const [timeFor, setTimeFor] = useState<number>();
  // an exercise missing from the glossary (cannot happen through the UI) shows all columns it has values for
  const f = e ? setFields(e) : { weight: true, reps: true, duration: true, distance: true };
  const opt = e && !props.session ? optionalTargets(e) : { reps: false, duration: false };
  // the time of a distance set may be empty in a template (no goal) and in a session (not taken)
  const timeOptional = e?.measure === "distance";
  const labels = setLabels(te.sets);
  const setValue = (i: number, patch: Partial<TemplateSet>) => props.onChange((x) => updateSet(x, i, patch));

  // the main target first, optional targets ("Ziel-…") last
  const required = requiredFields(e);
  const missing = (k: keyof TemplateSet, i: number) => !!props.missing?.includes(i) && (required as string[]).includes(k);

  type Column = { header: string; cell(s: TemplateSet, i: number): React.ReactNode };
  const reps: Column = {
    header: opt.reps ? t.template.colTargetReps : t.template.colReps,
    cell: (s, i) => <NumField label={t.template.colReps} value={s.reps} mode="numeric" parse={parseReps} format={formatNumber}
      placeholder={opt.reps ? t.template.noTarget : undefined} missing={missing("reps", i)} onChange={(v) => setValue(i, { reps: v })} />,
  };
  const duration: Column = {
    header: opt.duration ? t.template.colTargetDuration : t.template.colDuration,
    cell: (s, i) => (
      <button className={"field num time-cell" + (missing("durationSec", i) ? " invalid" : "")} aria-label={t.template.colDuration} onClick={() => setTimeFor(i)}>
        {s.durationSec === undefined ? <span className="placeholder">{t.template.noTarget}</span> : formatDuration(s.durationSec)}
      </button>
    ),
  };
  const weight: Column = {
    header: t.template.colWeight,
    cell: (s, i) => <NumField label={t.template.colWeight} value={s.weightKg} mode="decimal" parse={parseWeight} format={formatNumber}
      onChange={(v) => setValue(i, { weightKg: v })} />,
  };
  const distance: Column = {
    header: t.template.colDistance,
    cell: (s, i) => <NumField label={t.template.colDistance} value={s.distanceM} mode="decimal" parse={parseDistance} format={formatNumber}
      missing={missing("distanceM", i)} onChange={(v) => setValue(i, { distanceM: v })} />,
  };
  const columns = (props.session
    ? [f.weight && weight, f.distance && distance, ...(e?.measure === "timed" ? [duration, reps] : [f.reps && reps, f.duration && duration])]
    : [
      f.weight && weight, f.distance && distance,
      f.reps && !opt.reps && reps, f.duration && !opt.duration && duration,
      f.reps && opt.reps && reps, f.duration && opt.duration && duration,
    ]).filter((c): c is Column => !!c);
  const grid = { gridTemplateColumns: `64px repeat(${columns.length}, minmax(0, 1fr))` };
  const timeSet = timeFor !== undefined ? te.sets[timeFor] : undefined;

  return (
    <section ref={props.cardRef} className={"ex-card" + (props.collapsed ? " collapsed" : "") + (props.dragging ? " dragging" : "")}
      style={props.shift ? { transform: `translateY(${props.shift}px)` } : undefined}>
      <header className="ex-head">
        {props.handle && <span className="drag-handle" aria-label={t.template.dragHandle} role="button" {...props.handle}>≡</span>}
        <div className="ex-title">
          <div className="name">{e?.name ?? t.template.unknownExercise}{e?.hidden && <span className="badge">{t.template.hiddenBadge}</span>}</div>
          {e && <div className="sub">{t.equipment(e.equipment)} · {t.measure(e.measure)}</div>}
        </div>
        <button className="secondary icon" aria-label={t.template.removeExercise} onClick={props.onMenu}>⋯</button>
      </header>
      {!props.collapsed && (
        <>
        {!props.session && <div className="rest-row">
          <span className="muted-text">{t.template.rest}</span>
          <button className="secondary" disabled={te.restSec <= 0}
            onClick={() => props.onChange((x) => ({ ...x, restSec: Math.max(0, x.restSec - REST_STEP) }))}>−{REST_STEP}</button>
          <span className="rest-value">{formatDuration(te.restSec)}</span>
          <button className="secondary" disabled={te.restSec >= MAX_REST_SEC}
            onClick={() => props.onChange((x) => ({ ...x, restSec: Math.min(MAX_REST_SEC, x.restSec + REST_STEP) }))}>+{REST_STEP}</button>
        </div>}

        <div className="set-grid head" style={grid}>
          <span>{t.template.colSet}</span>
          {columns.map((c) => <span key={c.header}>{c.header}</span>)}
        </div>
        {te.sets.length === 0 && <p className="muted-text">{t.template.noSets}</p>}
        {te.sets.map((s, i) => (
          <div key={i} className="set-grid" style={grid}>
            <button className={"set-label " + s.type} onClick={() => props.onSetMenu(i)}>{labels[i]}</button>
            {columns.map((c) => <Fragment key={c.header}>{c.cell(s, i)}</Fragment>)}
          </div>
        ))}
        <button className="secondary add-set" onClick={() => props.onChange((x) => addSet(x, e ?? { measure: "reps" }))}>{t.template.addSet}</button>
        {timeSet && timeFor !== undefined && (
          <DurationPicker title={`${duration.header} · ${t.template.colSet} ${labels[timeFor]}`} value={timeSet.durationSec}
            fallback={timeOptional ? 60 : 40} optional={timeOptional} onCancel={() => setTimeFor(undefined)}
            onPick={(v) => { setValue(timeFor, { durationSec: v }); setTimeFor(undefined); }} />
        )}
        </>
      )}
    </section>
  );
}
